import axios from 'axios'

const shapeParamsRecursively = (params, formdata, namespace = '') => {
    ;(Array.isArray(params) ? params : Object.keys(params)).forEach((v, i) => {
        const key = Array.isArray(params) ? i : v

        if (params[key]) {
            if (
                typeof params[key] === 'object' &&
                !(params[key] instanceof FileList)
            ) {
                if (params[key].name && params[key].type) {
                    formdata.append(
                        namespace + (!namespace ? key : `[${key}]`),
                        params[key],
                    )
                }

                formdata = shapeParamsRecursively(
                    params[key],
                    formdata,
                    namespace + (!namespace ? key : `[${key}]`),
                )
            } else {
                if (params[key] instanceof FileList) {
                    let files = []
                    for (let fieldIndex in params[key]) {
                        let file = params[key][fieldIndex]
                        if (typeof file === 'object') {
                            let fileKey =
                                namespace +
                                (!namespace
                                    ? key
                                    : `[${key}]` + `[${fieldIndex}]`)
                            formdata.append(fileKey, file)
                        }
                    }
                }

                formdata.append(
                    namespace + (!namespace ? key : `[${key}]`),
                    params[key],
                )
            }
        }
    })

    namespace = ''

    return formdata
}

/**
 * Local storage Function Helpers
 * 
 * Get And Set Via Localstorage With Expiration Time (in replacement of cookies)
 * Apparently, 3rd-party cookie implementations are now unusable in iOS 14+
 */

const setStorage = (key, value, ttl = 86400000) => {
	const now = new Date()
	const ttlMs = Number(ttl)
	const hasValidTtl = Number.isFinite(ttlMs) && ttlMs > 0

	const item = {
		value: value,
		expiry: hasValidTtl ? now.getTime() + ttlMs : null,
	}

	window.localStorage.setItem(key, JSON.stringify(item))
}

export const getStorage = (key) => {
	const itemStr = window.localStorage.getItem(key)
	if (!itemStr) return null

	const item = JSON.parse(itemStr)
	const now = new Date()

	if (item.expiry !== null && now.getTime() > item.expiry) return removeStorage(key)
	return item.value
}

export const removeStorage = (key) => {
	window.localStorage.removeItem(key)
	return null
}

export const shapeFormData = (params = {}) => {
    return shapeParamsRecursively(params, new FormData())
}

export const postdata = (obj = {}) => {
    return shapeFormData(obj)
}

export const patchdata = (obj = {}) => {
    const form = shapeFormData(obj)
    form.append('_method', 'PATCH')
    return form
}

export const randomString = (length = 7) => {
    return Math.random().toString(36).substring(length)
}

export const slug = (value) => {
    if (!value) return value
    return value.toLowerCase().split(' ').join('-')
}

export const lowercase = (value) => {
    if (!value) return value
    return value.toLowerCase()
}

export const dashed = (value) => {
    if (!value) return value
    return value.replace(' ', '-')
}

export const downloadFile = (url, name) => {
    const fileTransfer = new FileTransfer()
    let uri = encodeURI(url)
    let storageLocation = ''

    switch (device.platform) {
        case 'Android':
            storageLocation = cordova.file.externalRootDirectory + 'Download/'
            break
        case 'iOS':
            storageLocation = cordova.file.documentsDirectory
            break
    }

    window.resolveLocalFileSystemURL(
        storageLocation,
        (fileSystem) => {
            fileTransfer.download(
                uri,
                fileSystem.nativeURL + name,
                (success) => {
                    window.plugins.toast.showWithOptions({
                        message:
                            'File named ' +
                            name +
                            " is successfully downloaded. Please check it in your internal memory's Download folder.",
                        duration: 'long',
                        position: 'bottom',
                        addPixelsY: -40,
                    })
                },

                (error) => {
                    window.plugins.toast.showWithOptions({
                        message:
                            'File Download Error: ' + JSON.stringify(error),
                        duration: 'long',
                        position: 'bottom',
                        addPixelsY: -40,
                    })
                },
            )
        },

        (error) => {
            window.plugins.toast.showWithOptions({
                message:
                    'ResolveLocalFileSystemURL Error: ' + JSON.stringify(error),
                duration: 'long',
                position: 'bottom',
                addPixelsY: -40,
            })
        },
    )
}

export const androidDownloadPermission = () => {
    const permissions = cordova.plugins.permissions

    permissions.checkPermission(permissions.READ_EXTERNAL_STORAGE, (status) => {
        if (!status.hasPermission) {
            permissions.requestPermission(
                permissions.READ_EXTERNAL_STORAGE,
                success,
                error,
            )

            function error() {
                window.plugins.toast.showWithOptions({
                    message:
                        "Oh No! We can't deliver your downloads right now.",
                    duration: 'long',
                    position: 'bottom',
                    addPixelsY: -40,
                })

                removeStorage('permitted')
            }

            function success(status) {
                if (!status.hasPermission) {
                    error()
                } else {
                    setStorage('permitted', 'true')
                }
            }
        } else {
            setStorage('permitted', 'true')
        }
    })
}

export const pushNotificationsPermission = (router) => {
    switch (device.platform) {
        case 'Android':
            const permissions = cordova.plugins.permissions
            permissions.checkPermission(
                permissions.POST_NOTIFICATIONS,
                (status) => {
                    if (status.hasPermission) {
                        setPushNotifications(router)
                    } else {
                        if (parseInt(device.version) >= 13) {
                            permissions.requestPermission(
                                permissions.POST_NOTIFICATIONS,
                                success,
                                error,
                            )

                            function success(status) {
                                if (status.hasPermission) {
                                    setPushNotifications(router)
                                } else {
                                    error()
                                }
                            }

                            function error() {
                                window.plugins.toast.showWithOptions({
                                    message:
                                        "Oh No! We can't push your notifications right now.",
                                    duration: 'long',
                                    position: 'bottom',
                                    addPixelsY: -40,
                                })
                            }
                        }
                    }
                },
            )
            break
        case 'iOS':
            setPushNotifications(router)
            break
        default:
            return null
    }
}

export const setPushNotifications = (router) => {
    const push = PushNotification.init({
        android: {
            icon: 'icon',
            iconColor: '#00b661',
            sound: false,
            vibrate: true,
            forceShow: true,
        },
        browser: {
            pushServiceURL: 'http://push.api.phonegap.com/v1/push',
        },
        ios: {
            alert: true,
            badge: true,
            sound: true,
            clearBadge: true,
        },
        windows: {},
    })

    push.on('registration', (data) => {
        setStorage('device_token', data.registrationId)
        setStorage('device_os', device.platform)
    })

    push.on('notification', (data) => {
        push.finish(
            async () => {
                if (device.platform == 'Android') {
                    router.push(data.additionalData.router)
                }
            },
            (error) => {
                window.plugins.toast.showWithOptions({
                    message:
                        'Push Finish Error For Notification ID ' +
                        data.additionalData.notId +
                        ': ' +
                        JSON.stringify(error),
                    duration: 'short',
                    position: 'bottom',
                    addPixelsY: -40,
                })
            },
            data.additionalData.notId,
        )
    })

    push.on('error', (err) => {
        window.plugins.toast.showWithOptions({
            message: 'Notification Error: ' + err.message,
            duration: 'long',
            position: 'bottom',
            addPixelsY: -40,
        })
    })
}

export const getMobileHeaderHeight = () => {
    var headerHeight = getComputedStyle(
        document.documentElement,
    ).getPropertyValue('--sat')
    // Return the height of the header
    return headerHeight
}

export const ABNValidityChecker = (abn) => {
    const digits = String(abn).replace(/\D+/g, '')
    if (!/^\d{11}$/.test(digits)) return false

    let totalWeight = 0
    let weighingFactor = 1

    for (let i = 0; i < digits.length; i++) {
        const digit = digits.charCodeAt(i) - 48 // faster than Number()
        
        if (i === 0) {
            totalWeight += (digit - 1) * 10
        } else {
            totalWeight += digit * weighingFactor
            weighingFactor += 2
        }
    }

    return totalWeight % 89 === 0
}

// Loose pre-check only — the API's phone:mobile,AU rule stays authoritative.
// Used to decide whether a stored number is worth auto-filling into a field
// the user cannot edit, where an invalid value would block validation.
export const isLikelyAuMobile = (value) => {
    const digits = String(value || '').replace(/\D+/g, '')
    return /^04\d{8}$/.test(digits) || /^614\d{8}$/.test(digits)
}

export const extractSubAddress = (address = null, end = null) => {
    const normalized = address.replace(/\s+/g, ' ')

    const endPos = normalized.toLowerCase().indexOf(end.toLowerCase())
    if (endPos === -1) {
        return address // end identifier not found, return original
    }

    const cutPos = normalized.lastIndexOf(',', endPos - 1)
    if (cutPos === -1) {
        return normalized.trim() // No comma found, don't trim
    }

    return normalized.slice(0, cutPos).trim() // Up to the comma before end
}

export const extractSuburb = (address = null, end = null) => {
    const normalized = address.trim().replace(/\s+/g, ' ')

    const endPos = normalized.toLowerCase().indexOf(end.toLowerCase())
    if (endPos === -1) {
        return null // end identifier not found
    }

    const firstComma = normalized.indexOf(',')
    if (firstComma === -1 || firstComma > endPos) {
        return null // No comma before end
    }

    const middle = normalized.slice(firstComma + 1, endPos)
    return middle.trim()
}

// Speech-to-text utilities for Cordova + Web fallback
// - Cordova: uses cordova-plugin-speechrecognition
// - Web: uses Web Speech API (webkitSpeechRecognition / SpeechRecognition)

const isCordova = typeof window.cordova !== 'undefined'

let isListeningActive = false		// Prevent overlapping listening sessions
let silenceTimeoutId = null			// Triggers after configured silence duration (final emit)
let voiceIdleTimeoutId = null		// Flips voice=false after brief inactivity
let latestPartialText = ''			// Most recent partial text (raw from engine)
let lastVoiceDetected = false		// Current voice detection state
let currentStopFunction = null		// Reused when start is called during an active session
let webRecognizer = null			// Active Web Speech recognizer instance (web only)

// New: track committed + seen partials to avoid duplicates in finals
let committedText = ''				// Text already finalized/emitted
let lastPartialSeen = ''			// Last partial processed (to drop exact repeats)

// Cordova plugin accessor
function getSpeechPlugin() {
	return window.plugins && window.plugins.speechRecognition
}

// Web Speech class accessor
function getWebRecognizerClass() {
	return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

// Normalize spaces for robust prefix comparison
function normalize(s) {
	return (s || '').replace(/\s+/g, ' ').trim()
}

// Compute what "new" text should be added based on the latest partial result
// compared to everything we've already committed.
//
// Idea:
// - Split both committed text and latest partial into words.
// - Walk from the start while words are identical (case-insensitive).
// - Anything after the common prefix is considered new "delta" text.
// - If there is no common prefix (or they are exactly the same), fall back to
//   simple rules to avoid duplicating text.
function getDeltaFromCommitted(latestPartialText) {
	// Normalize spaces and trim for both "already committed" and "latest" text
	const latestNormalized = normalize(latestPartialText)
	const committedNormalized = normalize(committedText)

	// If there is no latest text, nothing to add
	if (!latestNormalized) return ''

	// Split into word arrays (using spaces, since we already normalized)
	const latestWords = latestNormalized.split(' ')
	const committedWords = committedNormalized.split(' ')

	// Find longest common prefix (word by word, case-insensitive)
	let commonPrefixLength = 0
	while (
		commonPrefixLength < latestWords.length &&
		commonPrefixLength < committedWords.length &&
		latestWords[commonPrefixLength].toLowerCase() === committedWords[commonPrefixLength].toLowerCase()
	) {
		commonPrefixLength++
	}

	// If we found at least one common word and there are extra words in the latest text,
	// treat only those extra words as the "new" delta.
	if (commonPrefixLength > 0 && commonPrefixLength < latestWords.length) {
		return latestWords.slice(commonPrefixLength).join(' ')
	}

	// If the normalized strings are exactly the same, there is no new text
	if (latestNormalized.toLowerCase() === committedNormalized.toLowerCase()) return ''

	// Fallback:
	// - Either there was no common prefix, or latest text is a significant rewrite.
	// - In that case, return the full latest text so the caller can decide
	//   whether to replace or append.
	return latestNormalized
}

/**
 * Request mic/speech permission
 * - Cordova: ask via plugin
 * - Web: permission is requested on start(), so resolve here
 */
export function requestSpeechPermission() {
	return new Promise((resolve, reject) => {
		if (isCordova) {
			const plugin = getSpeechPlugin()
			if (!plugin) return reject(new Error('cordova-plugin-speechrecognition not available'))
			plugin.hasPermission(has => {
				if (has) return resolve()
				plugin.requestPermission(resolve, reject)
			}, reject)
		} else {
			const Recognizer = getWebRecognizerClass()
			if (!Recognizer) return reject(new Error('Web Speech API not available in this environment'))
			resolve()
		}
	})
}

/**
 * Start listening and streaming STT.
 * Idempotent: if already active, returns the existing stop function without restarting.
 *
 * Callbacks:
 * - onPartial(text)
 * - onFinal(text)
 * - onDetectVoice(detected: boolean)
 * - onStatusChange('listening'|'stopped')
 * - onError(error)
 *
 * Options:
 * - language: BCP-47 (default 'en-US')
 * - silenceMs: emit final after this silence (default 3000)
 * - voiceIdleMs: flip voice=false after inactivity (default 600)
 * - showPopup/showPartial/matches/prompt: Cordova plugin options
 */
export function startSpeechToText(
	{
		language = 'en-US',
		silenceMs = 3000,
		voiceIdleMs = 600,
		showPopup = false,
		showPartial = true,
		matches = 1,
		prompt = 'Speak now'
	} = {},
	{
		onPartial,
		onFinal,
		onDetectVoice,
		onStatusChange,
		onError
	} = {}
) {
	// If active, return current stopper
	if (isListeningActive && typeof currentStopFunction === 'function') {
		return currentStopFunction
	}

	isListeningActive = true
	latestPartialText = ''
	lastVoiceDetected = false
	// Reset duplication trackers for a fresh session
	committedText = ''
	lastPartialSeen = ''
	onStatusChange && onStatusChange('listening')

	// Emit voice state only on change
	const setVoiceDetected = (detected) => {
		if (lastVoiceDetected === detected) return
		lastVoiceDetected = detected
		onDetectVoice && onDetectVoice(!!detected)
	}

	// Debounced finalizer based on silence
	const scheduleSilenceFinalize = () => {
		if (silenceTimeoutId) clearTimeout(silenceTimeoutId)
		if (!silenceMs) return
		silenceTimeoutId = setTimeout(() => {
			if (!isListeningActive) return

			// Mark silence reached
			setVoiceDetected(false)
			if (voiceIdleTimeoutId) {
				clearTimeout(voiceIdleTimeoutId)
				voiceIdleTimeoutId = null
			}

			// Emit only new text beyond what’s already committed
			const delta = getDeltaFromCommitted(latestPartialText)
			if (delta && onFinal) onFinal(delta)

			// Update committed to the full latest partial and reset duplicate guard
			committedText = normalize(latestPartialText)
			lastPartialSeen = committedText

			// For Cordova, begin next phrase cycle if engine ended
			if (isCordova) beginCordovaCycle()
		}, silenceMs)
	}

	// Brief inactivity to flip voice=false for UI responsiveness
	const scheduleVoiceIdle = () => {
		if (voiceIdleTimeoutId) clearTimeout(voiceIdleTimeoutId)
		if (!voiceIdleMs) return
		voiceIdleTimeoutId = setTimeout(() => {
			if (!isListeningActive) return
			setVoiceDetected(false)
		}, voiceIdleMs)
	}

	// Cordova path
	const beginCordovaCycle = () => {
	if (!isCordova || !isListeningActive) return
		const plugin = getSpeechPlugin()
		if (!plugin) {
			onError && onError(new Error('cordova-plugin-speechrecognition not available'))
			return
		}
		const pluginOptions = { language, matches, prompt, showPopup, showPartial }
		try {
			plugin.startListening(
				(matchesArray) => {
					// Any result -> mark voice active
					setVoiceDetected(true)
					scheduleVoiceIdle()

					// Build partial text
					const text = Array.isArray(matchesArray) ? (matchesArray[0] || '') : String(matchesArray || '')

					// Drop exact duplicates to reduce churn
					if (text === lastPartialSeen) return
					lastPartialSeen = text

					latestPartialText = text
					onPartial && onPartial(text)

					// Debounce final emit
					scheduleSilenceFinalize()
				},
				(error) => {
					onError && onError(error)
					// Keep session alive on benign errors
					if (isListeningActive) setTimeout(beginCordovaCycle, 250)
				},
				pluginOptions
			)
		} catch (error) {
			onError && onError(error)
			if (isListeningActive) setTimeout(beginCordovaCycle, 250)
		}
	}

	// Web path
	const beginWebCycle = () => {
		if (isCordova || !isListeningActive) return
		const Recognizer = getWebRecognizerClass()
		if (!Recognizer) {
			onError && onError(new Error('Web Speech API not available in this environment'))
			return
		}

		// Create one recognizer instance, continuous + interim
		webRecognizer = new Recognizer()
		webRecognizer.lang = language
		webRecognizer.continuous = true
		webRecognizer.interimResults = true
		try { webRecognizer.maxAlternatives = 1 } catch (_) {}

		// Handle results (interim + final)
		webRecognizer.onresult = (event) => {
			// Any result implies activity
			setVoiceDetected(true)
			scheduleVoiceIdle()

			// Build a human-readable interim string from the last batch
			let combined = ''
			for (let i = event.resultIndex; i < event.results.length; i++) {
				combined += event.results[i][0].transcript
			}
			const text = combined.trim()

			// Drop exact duplicates to reduce churn
			if (text === lastPartialSeen) return
			lastPartialSeen = text

			latestPartialText = text
			onPartial && onPartial(text)

			// Silence-based finalization
			scheduleSilenceFinalize()
		}

		webRecognizer.onspeechstart = () => setVoiceDetected(true)
		webRecognizer.onspeechend = () => scheduleVoiceIdle()

		webRecognizer.onerror = (e) => {
			onError && onError(e)
			// Optional: auto-restart on transient errors
		}

		webRecognizer.onend = () => {
			// Browser may end after a phrase or permission issues.
			// If still active, attempt to resume to emulate continuous mode.
			if (isListeningActive) {
				try { webRecognizer.start() } catch (_) {}
			}
		}

		try {
			webRecognizer.start()
		} catch (e) {
			onError && onError(e)
		}
	}

	// Create and store the stopper so repeated start calls can return it
	currentStopFunction = () => stopSpeechToText({ onStatusChange, onDetectVoice })

	// Kick off platform-specific listening
	if (isCordova) beginCordovaCycle()
	else beginWebCycle()

	return currentStopFunction
}

/**
 * Stop listening and cleanup state/timers/resources
 */
export function stopSpeechToText({ onStatusChange, onDetectVoice } = {}) {
	isListeningActive = false

	if (silenceTimeoutId) {
		clearTimeout(silenceTimeoutId)
		silenceTimeoutId = null
	}
	if (voiceIdleTimeoutId) {
		clearTimeout(voiceIdleTimeoutId)
		voiceIdleTimeoutId = null
	}

	latestPartialText = ''
	committedText = ''
	lastPartialSeen = ''

	// Cordova cleanup
	if (isCordova) {
		const plugin = getSpeechPlugin()
		try { plugin && plugin.stopListening(() => {}, () => {}) } catch (_) {}
	} else {
		// Web cleanup
		if (webRecognizer) {
			try {
				webRecognizer.onresult = null
				webRecognizer.onerror = null
				webRecognizer.onend = null
				webRecognizer.onspeechstart = null
				webRecognizer.onspeechend = null
				webRecognizer.stop()
			} catch (_) {}
			webRecognizer = null
		}
	}

	// Reflect silence and status to UI
	if (typeof onDetectVoice === 'function') onDetectVoice(false)
	onStatusChange && onStatusChange('stopped')
	currentStopFunction = null
}

/**
 * This function takes a string value and encodes it into a versioned hexadecimal string.
 * It first converts the string to a Uint8Array using the TextEncoder.encode() method.
 * Then it loops through each element of the Uint8Array and converts it to a hexadecimal
 * string using the toString(16) method. The resulting hexadecimal string is padded with
 * leading zeros to ensure that each hexadecimal number has a length of 2 characters.
 * Finally, it concatenates all the hexadecimal strings together with a 'v1-' prefix to
 * indicate the version of the encoding.
 * 
 * @param {string} value - The string value to be encoded. If null or undefined, an empty
 * string is used as the value.
 * @returns {string} - The encoded versioned hexadecimal string.
 */
export const encodeFingerprintSecret = (value) => {
    const bytes = new TextEncoder().encode(String(value ?? ''))
    let out = ''

    for (let i = 0; i < bytes.length; i++) {
        out += bytes[i].toString(16).padStart(2, '0')
    }

    return `v1-${out}`
}

/**
 * This function takes a versioned hexadecimal string and decodes it into the original
 * string value. It first checks if the string starts with 'v1-' to determine the
 * version of the encoding. If it does, it extracts the hexadecimal string by slicing the
 * string from index 3. If the string does not start with 'v1-', it assumes that the
 * string is already a hexadecimal string.
 * 
 * Next, it checks if the hexadecimal string is valid by ensuring that it is not empty
 * and has an even number of characters. If the string is not valid, it returns null.
 * 
 * Then, it converts the hexadecimal string into a Uint8Array by creating a new Uint8Array
 * with a length equal to half the length of the hexadecimal string. It then loops through
 * each character pair in the hexadecimal string and converts it to a numeric value using
 * parseInt(). This numeric value is then assigned to the corresponding element of the
 * Uint8Array.
 * 
 * Finally, it uses the TextDecoder.decode() method to convert the Uint8Array back into a
 * string. If the decoding fails, it returns null. Otherwise, it returns the decoded
 * string.
 * 
 * @param {string} value - The versioned hexadecimal string to be decoded. If null or
 * undefined, an empty string is used as the value.
 * @returns {string|null} - The decoded original string value. If the decoding fails,
 * null is returned.
 */
export const decodeFingerprintSecret = (value) => {
    const raw = String(value ?? '')
    const hex = raw.startsWith('v1-') ? raw.slice(3) : raw

    if (!hex || hex.length % 2 !== 0) {
        return null
    }

    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
        bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16)
    }

    try {
        return new TextDecoder().decode(bytes)
    } catch {
        return null
    }
}

/**
 * This function generates a random secret key for the device.
 * It creates a new Uint8Array with a length of 32 bytes.
 * 
 * If the browser supports the Web Crypto API, it uses the
 * getRandomValues() method to fill the Uint8Array with
 * cryptographically secure random values.
 * 
 * If the browser does not support the Web Crypto API, it uses
 * the Math.random() method to fill the Uint8Array with random
 * values in the range of 0 to 255.
 * 
 * The function then converts each byte of the Uint8Array to a
 * hexadecimal string using the toString(16) method. The
 * resulting hexadecimal string is padded with leading zeros
 * to ensure that each hexadecimal number has a length of 2
 * characters.
 * 
 * Finally, it returns the concatenated hexadecimal strings
 * as the generated device secret key.
 * 
 * @returns {string} The generated device secret key as a hexadecimal string.
 */
export const generateDeviceSecret = () => {
    const bytes = new Uint8Array(32)

    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        // Use the Web Crypto API to generate cryptographically secure random values
        crypto.getRandomValues(bytes)
    } else {
        // Use the Math.random() method to generate random values
        for (let i = 0; i < bytes.length; i++) {
            bytes[i] = Math.floor(Math.random() * 256)
        }
    }

    let hex = ''
    for (let i = 0; i < bytes.length; i++) {
        // Convert each byte to a hexadecimal string and pad with leading zeros
        hex += bytes[i].toString(16).padStart(2, '0')
    }

    return hex
}